// Query membership only: no message bodies, attachments or read tokens.
const TTL = 20_000;
const MAX_QUERIES = 10;
const MAX_IDS = 100;
export const createQueryCacheState = () => ({
  queries: {}, activeKey: null, activeIds: [], identityEpoch: 0,
  permissionsEpoch: 0, sequence: 0, revision: 0,
});
export const cacheFresh = (entry, now = Date.now()) => Boolean(
  entry && entry.fetchedAt != null && !entry.dirty && !entry.overflow &&
  now - entry.fetchedAt < TTL
);
const entryFor = (state, key) => {
  if (!state.queries[key]) state.queries[key] = {
    orderedIds: [], pageIds: {}, meta: {}, hasMore: true, nextPage: 1,
    fetchedAt: null, dirty: true, requestId: null, loading: false, error: false,
  };
  state.queries[key].usedAt = ++state.sequence;
  const keys = Object.keys(state.queries).sort((a, b) => state.queries[a].usedAt - state.queries[b].usedAt);
  while (keys.length > MAX_QUERIES) delete state.queries[keys.shift()];
  return state.queries[key];
};
export const mutations = {
  activate(state, key) {
    state.activeKey = key;
    state.activeIds = key ? [...entryFor(state, key).orderedIds] : [];
  },
  begin(state, { key, requestId }) {
    const entry = entryFor(state, key);
    Object.assign(entry, { requestId, loading: true, error: false, revision: state.revision });
  },
  succeed(state, { key, requestId, page, ids, meta, replace = page === 1, pageIds, now = Date.now() }) {
    const entry = state.queries[key];
    if (!entry || entry.requestId !== requestId) return;
    const previous = replace ? [] : state.activeKey === key ? state.activeIds : entry.orderedIds;
    const orderedIds = [...new Set([...previous, ...ids])];
    const hasMore = typeof meta.has_more === 'boolean' ? meta.has_more : ids.length > 0;
    // Only retain numeric list counts and pagination, never arbitrary payload metadata.
    const counts = Object.fromEntries(Object.entries(meta).filter(([name, value]) =>
      ['mine_count', 'assigned_count', 'unassigned_count', 'all_count'].includes(name) && Number.isFinite(value)));
    Object.assign(entry, { orderedIds: orderedIds.slice(0, MAX_IDS), meta: counts,
      hasMore, nextPage: hasMore ? (meta.next_page || page + 1) : null,
      page, fetchedAt: now, dirty: entry.revision !== state.revision || (!replace && entry.dirty),
      overflow: orderedIds.length > MAX_IDS, loading: false, error: false });
    if (replace) entry.pageIds = {};
    if (!entry.overflow) Object.assign(entry.pageIds, pageIds || { [page]: [...ids] });
    if (state.activeKey === key) state.activeIds = orderedIds;
  },
  fail(state, { key, requestId }) {
    const entry = state.queries[key];
    if (entry?.requestId === requestId) Object.assign(entry, { loading: false, error: true, dirty: true });
  },
  dirty(state) {
    state.revision += 1;
    Object.values(state.queries).forEach(entry => { entry.dirty = true; });
  },
  clear(state) {
    state.queries = {}; state.activeKey = null; state.activeIds = [];
    state.identityEpoch += 1; state.permissionsEpoch += 1; state.revision += 1;
  },
};
export default { namespaced: true, state: createQueryCacheState, mutations };
